import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import WpDynamicTable from "../../Components/WhatsappTable/WpDynamicTable";
import {
  AddGeoResolverDetail,
  DeleteGeoResolverDetail,
  fetchGeoResolverDetail,
  fetchGeoResolverMain,
  UpdateGeoResolverDetail,
} from "../../Redux/WhatsappSlice/GeoResolver";
import Loader from "../../Components/Loader/Loader";

const GeoResolverDetail = () => {
  const dispatch = useDispatch();
  const { GeoResolverDetailData, GeoResolverDetailLoading, GeoResolverMainData } = useSelector(
    (state) => state.GeoResolver
  );

  const [formData, setFormData] = useState({
    Status_Key: "",
    Results_Key: "",
    Geometry_Key: "",
    Location_Key: "",
    Lat_Key: "",
    Lng_Key: "",
  });

  const [selectedProviderId, setSelectedProviderId] = useState("");
  const [editId, setEditId] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    dispatch(fetchGeoResolverDetail());
    dispatch(fetchGeoResolverMain());
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const errs = {};
    if (!selectedProviderId && !editId) errs.Provider = "Please select a Provider";
    if (!formData.Status_Key || formData.Status_Key.trim() === "") errs.Status_Key = "Status Key is required";
    if (!formData.Results_Key || formData.Results_Key.trim() === "") errs.Results_Key = "Results Key is required";
    if (!formData.Geometry_Key || formData.Geometry_Key.trim() === "") errs.Geometry_Key = "Geometry Key is required";
    if (!formData.Location_Key || formData.Location_Key.trim() === "") errs.Location_Key = "Location Key is required";
    if (!formData.Lat_Key || formData.Lat_Key.trim() === "") errs.Lat_Key = "Lat Key is required";
    if (!formData.Lng_Key || formData.Lng_Key.trim() === "") errs.Lng_Key = "Lng Key is required";
    return errs;
  };

  const resetForm = () => {
    setFormData({
      Status_Key: "",
      Results_Key: "",
      Geometry_Key: "",
      Location_Key: "",
      Lat_Key: "",
      Lng_Key: "",
    });
    setSelectedProviderId("");
    setEditId(null);
    setErrors({});
  };

  const handleEdit = (row) => {
    setEditId(row.DetailId);
    setSelectedProviderId(row.ProviderId || "");
    setFormData({
      Status_Key: row.Status_Key || "",
      Results_Key: row.Results_Key || "",
      Geometry_Key: row.Geometry_Key || "",
      Location_Key: row.Location_Key || "",
      Lat_Key: row.Lat_Key || "",
      Lng_Key: row.Lng_Key || "",
    });
  };

  const handleDelete = async (row) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    try {
      await dispatch(DeleteGeoResolverDetail({ id: row.DetailId })).unwrap();
      toast.success("Deleted successfully!", { position: "top-right", autoClose: 1000 });
      dispatch(fetchGeoResolverDetail());
    } catch (error) {
      toast.error(`Failed to delete: ${error}`, { position: "top-right", autoClose: 1500 });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      if (editId) {
        const payload = { ...formData, Provider: selectedProviderId };
        await dispatch(UpdateGeoResolverDetail({ id: editId, formData: payload })).unwrap();
        toast.success("GeoResolver Detail updated successfully!", { position: "top-right", autoClose: 1000 });
      } else {
        await dispatch(AddGeoResolverDetail({ selectedId: selectedProviderId, values: formData })).unwrap();
        toast.success("GeoResolver Detail added successfully!", { position: "top-right", autoClose: 1000 });
      }
      resetForm();
      dispatch(fetchGeoResolverDetail());
    } catch (error) {
      toast.error(`Failed to ${editId ? "update" : "add"}: ${error?.message || "Unknown error"}`, {
        position: "top-right",
        autoClose: 1500,
      });
    }
  };

  const columns = [
    { field: "id", headerName: "No", flex: 0.5 },
    { field: "ProviderName", headerName: "Provider", flex: 1 },
    { field: "Status_Key", headerName: "Status Key", flex: 1 },
    { field: "Results_Key", headerName: "Results Key", flex: 1 },
    { field: "Geometry_Key", headerName: "Geometry Key", flex: 1 },
    { field: "Location_Key", headerName: "Location Key", flex: 1 },
    { field: "Lat_Key", headerName: "Lat Key", flex: 0.8 },
    { field: "Lng_Key", headerName: "Lng Key", flex: 0.8 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <div className="flex space-x-2 justify-center">
          <button
            className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
            onClick={() => handleEdit(params.row)}
          >
            Update
          </button>
          <button
            className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
            onClick={() => handleDelete(params.row)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  const rows = (GeoResolverDetailData || []).map((el, index) => ({
    id: index + 1,
    DetailId: el._id,
    ProviderId: el?.Provider?._id || "",
    ProviderName: el?.Provider?.Provider || "-",
    Status_Key: el?.Status_Key,
    Results_Key: el?.Results_Key,
    Geometry_Key: el?.Geometry_Key,
    Location_Key: el?.Location_Key,
    Lat_Key: el?.Lat_Key,
    Lng_Key: el?.Lng_Key,
  }));

  const sortedRows = [...rows]
    .sort((a, b) => (a.ProviderName || "").localeCompare(b.ProviderName || "", undefined, { sensitivity: 'base' }))
    .map((item, idx) => ({ ...item, id: idx + 1 }));

  const sortedProviders = GeoResolverMainData
    ? [...GeoResolverMainData].sort((a, b) =>
        (a.Provider || a.Base_Url || "").localeCompare(b.Provider || b.Base_Url || "", undefined, { sensitivity: 'base' })
      )
    : [];

  if (GeoResolverDetailLoading) return <Loader />;

  return (
    <>
      <div>
        <div className="title">
          <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
            {editId ? "Update GeoResolver Detail" : "Add GeoResolver Detail"}
          </h3>
        </div>

        <div className="Form_bg_color bg-white shadow-sm rounded-xl mt-4">
          <form
            onSubmit={handleSubmit}
            className="grid grid-cols-12 gap-4 p-3 rounded-2xl"
          >
            {/* Provider Dropdown */}
            <div className="col-span-12 lg:col-span-4 md:col-span-6">
              <h5 className="mb-1 block font-semibold">Select Provider</h5>
              <select
                value={selectedProviderId}
                onChange={(e) => setSelectedProviderId(e.target.value)}
                className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded"
              >
                <option value="">-- Select Provider --</option>
                {sortedProviders.map((item) => (
                  <option key={item._id} value={item._id}>
                    {item.Provider || item.Base_Url}
                  </option>
                ))}
              </select>
              {errors.Provider && <p className="text-red-500 text-sm mt-1">{errors.Provider}</p>}
            </div>

            {/* Status Key */}
            <div className="col-span-12 lg:col-span-4 md:col-span-6">
              <h5 className="mb-1 block font-semibold">Status Key</h5>
              <input
                type="text"
                name="Status_Key"
                value={formData.Status_Key}
                onChange={handleChange}
                className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded"
              />
              {errors.Status_Key && <p className="text-red-500 text-sm mt-1">{errors.Status_Key}</p>}
            </div>

            {/* Results Key */}
            <div className="col-span-12 lg:col-span-4 md:col-span-6">
              <h5 className="mb-1 block font-semibold">Results Key</h5>
              <input
                type="text"
                name="Results_Key"
                value={formData.Results_Key}
                onChange={handleChange}
                className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded"
              />
              {errors.Results_Key && <p className="text-red-500 text-sm mt-1">{errors.Results_Key}</p>}
            </div>

            {/* Geometry Key */}
            <div className="col-span-12 lg:col-span-4 md:col-span-6">
              <h5 className="mb-1 block font-semibold">Geometry Key</h5>
              <input
                type="text"
                name="Geometry_Key"
                value={formData.Geometry_Key}
                onChange={handleChange}
                className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded"
              />
              {errors.Geometry_Key && <p className="text-red-500 text-sm mt-1">{errors.Geometry_Key}</p>}
            </div>

            {/* Location Key */}
            <div className="col-span-12 lg:col-span-4 md:col-span-6">
              <h5 className="mb-1 block font-semibold">Location Key</h5>
              <input
                type="text"
                name="Location_Key"
                value={formData.Location_Key}
                onChange={handleChange}
                className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded"
              />
              {errors.Location_Key && <p className="text-red-500 text-sm mt-1">{errors.Location_Key}</p>}
            </div>

            {/* Lat Key */}
            <div className="col-span-12 lg:col-span-2 md:col-span-6">
              <h5 className="mb-1 block font-semibold">Lat Key</h5>
              <input
                type="text"
                name="Lat_Key"
                value={formData.Lat_Key}
                onChange={handleChange}
                className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded"
              />
              {errors.Lat_Key && <p className="text-red-500 text-sm mt-1">{errors.Lat_Key}</p>}
            </div>

            {/* Lng Key */}
            <div className="col-span-12 lg:col-span-2 md:col-span-6">
              <h5 className="mb-1 block font-semibold">Lng Key</h5>
              <input
                type="text"
                name="Lng_Key"
                value={formData.Lng_Key}
                onChange={handleChange}
                className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded"
              />
              {errors.Lng_Key && <p className="text-red-500 text-sm mt-1">{errors.Lng_Key}</p>}
            </div>

            {/* Submit */}
            <div className="submit_Form col-span-12 flex justify-center">
              <button type="submit" className="All_button">
                {editId ? "Update" : "Submit"}
              </button>
              {editId && (
                <button
                  type="button"
                  className="bg-gray-500 text-white ms-2 px-4 py-2 rounded hover:bg-gray-600"
                  onClick={resetForm}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="px-2 py-6">
          <div className="title">
            <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
              View GeoResolver Detail
            </h3>
          </div>
          <div className="rounded-lg overflow-x-auto mt-3">
            <WpDynamicTable columns={columns} rows={sortedRows} height="550px" />
          </div>
        </div>
      </div>
    </>
  );
};

export default GeoResolverDetail;
